import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, ElementRef, Injectable, inject } from '@angular/core';
import {
  AirportDropDown,
  AirportDropDownList,
  AirportDropdownModalComponent
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AirportDropdownOverlayService {
  private overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;
  private currentElementRef: ElementRef | null = null;
  private closeSubject = new Subject<void>();
  private airportSelectedSubject = new Subject<AirportDropDownList>();
  private searchValueSubject = new Subject<string>();
  readonly close$ = this.closeSubject.asObservable();
  readonly airportSelected$ = this.airportSelectedSubject.asObservable();
  readonly searchValue$ = this.searchValueSubject.asObservable();
  private componentRef: ComponentRef<AirportDropdownModalComponent> | null =
    null;

  open(
    elementRef: ElementRef,
    content: AirportDropDown,
    selectedValue?: AirportDropDownList
  ): void {
    if (this.overlayRef?.hasAttached()) {
      this.currentElementRef = elementRef;
      this.updateContent(content);
      return;
    }
    this.currentElementRef = elementRef;
    const positionStrategy = this.getPositionStrategy(elementRef);
    const isMobile = window.innerWidth <= 768;
    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'airport-dropdown-overlay-panel',
      ...(isMobile && {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh'
      })
    });

    const portal = new ComponentPortal(AirportDropdownModalComponent);
    this.componentRef = this.overlayRef.attach(portal);
    this.componentRef.instance.content = content;
    this.componentRef.instance.selectedValue = selectedValue;
    setTimeout(() => {
      this.overlayRef?.backdropClick().subscribe(() => this.close());
      (document.querySelector('.search__input') as HTMLInputElement)?.select();
    }, 100);
    this.setupSubscriptions();
  }

  close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.currentElementRef = null;
      this.closeSubject.next();
      if (this.componentRef) {
        this.componentRef.instance.ngOnDestroy();
      }
    }
  }

  updateContent(content: AirportDropDown): void {
    if (this.componentRef) {
      this.componentRef.instance.content = content;
      this.componentRef.instance.setItem(
        content.airportList.items,
        content.airportList.id
      );
    }
  }

  updateSearchError(errorMessage: string): void {
    if (this.componentRef) {
      this.componentRef.instance.searchErrorMessage = errorMessage;
      if (this.componentRef.instance.searchErrorMessage.length > 0)
        this.componentRef.instance.searchInstance.hide();
    }
  }

  isOpen(): boolean {
    return this.overlayRef?.hasAttached() ?? false;
  }

  updatePosition(elementRef: ElementRef): void {
    if (this.overlayRef && this.componentRef) {
      const content = this.componentRef.instance.content;
      this.close();
      this.open(elementRef, content);
    }
  }

  private setupSubscriptions(): void {
    if (!this.componentRef) return;
    this.componentRef.instance.closeButton.subscribe(() => this.close());
    this.componentRef.instance.airportSelected.subscribe(
      (airport: AirportDropDownList) => {
        this.airportSelectedSubject.next(airport);
        this.close();
      }
    );
    this.componentRef.instance.searchValueEmit.subscribe(
      (searchTerm: string) => {
        this.searchValueSubject.next(searchTerm);
      }
    );
  }

  private getPositionStrategy(elementRef: ElementRef): PositionStrategy {
    const isMobile = window.innerWidth <= 991;
    if (isMobile) {
      return this.overlay.position().global().top('0').left('0');
    }

    const elements = document.querySelectorAll(
      '#flight-status-airport-code, .vacation-widget__from-and-to, .check-in-widget__from-and-to'
    );
    const fromToElement = Array.from(elements).find(
      (el) => (el as HTMLElement).offsetParent !== null
    ) as HTMLElement;
    const targetElement = fromToElement || elementRef.nativeElement;
    return this.overlay
      .position()
      .flexibleConnectedTo(targetElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8
        }
      ]);
  }
}
